import { getDemoName } from '../hooks/useDemoMode';
import api from './api';
import authService from '@/api/authService';
import errorToString from 'common/misc/errorToString';
import { AppError } from 'common/types/errors';
import { Substitute } from 'common/types/substitute';
import { EntitledUiUser, UiUser } from 'common/types/user';
import { UserSettings } from 'common/types/userSettings';
import { WorkflowState } from 'common/types/workflow';
import { createCache } from 'cross-state';
import { queued } from 'schummar-queue';
import { z } from 'zod';

export const userApi = new (class {
    list = createCache(
        async () => {
            try {
                const res = await api.get('/api/users').json();
                let users = z.array(UiUser).parse(res);

                if (import.meta.env.VITE_DEMO) {
                    users = users.map((user) => ({
                        ...user,
                        name: getDemoName(user.name),
                    }));
                }

                return users;
            } catch (e) {
                console.error('Failed to fetch users:', e);
                throw new AppError('Failed to fetch users', { cause: errorToString(e) });
            }
        },
        { invalidateAfter: { minutes: 10 } },
    );

    useList({ accountEnabled }: { accountEnabled?: boolean } = {}) {
        return this.list
            .mapValue((users) =>
                users?.filter(
                    (u) => accountEnabled === undefined || !!u.accountEnabled === !!accountEnabled,
                ),
            )
            .useCache();
    }

    lookup = this.list.mapValue((users) => {
        return new Map(users?.map((u) => [u.id, u]));
    });

    get = createCache(
        async (props: string | { managerOf: string }) => {
            const userId = typeof props === 'string' ? props : props.managerOf;
            try {
                const users = await this.list.get(undefined);
                const user = users?.find((x) => x.id === userId);
                if (typeof props === 'string') return user;
                return users.find((x) => x.id === user?.managerId);
            } catch (e) {
                console.error(`Failed to fetch user with id ${userId}`, e);
                throw new AppError('Failed to fetch user', { cause: errorToString(e) });
            }
        },
        { invalidateAfter: { minutes: 10 } },
    );

    getEntitledUsers = createCache(
        async ({ ownerId, recursive }: { ownerId: string; recursive?: boolean }) => {
            try {
                const res = await api
                    .get(`/api/users/${ownerId}/managers`, {
                        searchParams: {
                            recursive: recursive?.toString(),
                        },
                    })
                    .json();

                return z.partialRecord(WorkflowState, z.array(EntitledUiUser)).parse(res);
            } catch (e) {
                console.error(
                    `Failed to fetch entitled users for bucket with ownerId=${ownerId},recursive=${recursive}`,
                    e,
                );
                throw new AppError('Failed to fetch managers', { cause: errorToString(e) });
            }
        },
        { invalidateAfter: { minutes: 10 } },
    );

    updateSettings = queued(async (settings: UserSettings) => {
        await api.patch('/api/users/settings', { json: settings }).json();
        await authService.appLogin.get({ update: 'force' });
    });

    updateAdditionalAppovers = queued(async (userId: string, approverIds: string[] | null) => {
        await api.post(`/api/users/${userId}/additionalApprovers`, { json: approverIds });
        authService.appLogin.invalidate();
        this.get.invalidateAll();
        this.getEntitledUsers.invalidateAll();
        await this.list.get({ update: 'force' });
    });

    updateSubstitute = queued(async (userId: string, substitute: Substitute | null) => {
        await api.post(`/api/users/${userId}/substitute`, { json: substitute });
        await authService.appLogin.get({ update: 'force' });
        this.get.invalidateAll();
        this.getEntitledUsers.invalidateAll();
        await this.list.get({ update: 'force' });
    });

    deleteSubstitute = queued(async (userId: string, substituteId: string) => {
        await api.delete(`/api/users/${userId}/substitute/${substituteId}`);
        await authService.appLogin.get({ update: 'force' });
        this.get.invalidateAll();
        this.getEntitledUsers.invalidateAll();
        await this.list.get({ update: 'force' });
    });

    async sync() {
        await api.post('/api/users/sync');
    }

    async testEmail() {
        await api.post('/api/users/email/test');
    }
})();
export default userApi;

Object.assign(window, { userApi });
